import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/common";
import { CheckCircle2, Circle, ArrowRight, Rocket, X } from "lucide-react";

const KEY = (uid) => `gfs_getting_started_hidden_${uid || "anon"}`;

// Role/capability-aware starter checklist. Kept to 4–6 items, plain language,
// completion derived only where reliable (Creator items from the user's Insights).
function buildChecklist({ role, caps, uid, insights, meetings }) {
  const mine = (insights || []).filter((i) => i.created_by === uid);
  const isAdmin = role === "administrator";
  const canRecords = caps.includes("records_management");
  const canExport = caps.includes("data_export");

  if (isAdmin) {
    return {
      title: "Getting started",
      subtitle: "Orient yourself in a few steps. You can start anywhere.",
      items: [
        { label: "Review organization settings", to: "/admin" },
        { label: "Review users and departments", to: "/admin" },
        { label: "Review Governing Body configuration", to: "/admin" },
        { label: "Review workflow configuration", to: "/admin" },
        { label: "Check system readiness", to: "/admin" },
      ],
    };
  }
  if (canRecords) {
    const items = [
      { label: "Open Records", to: "/records" },
      { label: "Review retention policies", to: "/records" },
      { label: "Review upcoming disposition items", to: "/records" },
      { label: "Review active Holds", to: "/records" },
    ];
    if (canExport) items.push({ label: "Export authorized records", to: "/records" });
    return { title: "Getting started", subtitle: "Govern official records and export your data.", items };
  }
  if (role === "manager") {
    return {
      title: "Getting started",
      subtitle: "My Work is where items waiting on you appear.",
      items: [
        { label: "Open My Work", to: "/my-work", done: true },
        { label: "Review Insights needing approval", to: "/my-work" },
        { label: "Review upcoming meetings", to: "/meetings" },
        { label: "Open a meeting agenda", to: "/meetings" },
        { label: "Run Roll Call and confirm quorum", to: "/meetings" },
      ],
    };
  }
  if (role === "analyst") {
    return {
      title: "Getting started",
      subtitle: "Create and track government items. My Work shows everything waiting for you.",
      items: [
        { label: "Sign in to GovFlowState", done: true },
        { label: "Review your work", to: "/my-work", done: true },
        { label: "Create your first Insight", to: "/insights/new", done: mine.length > 0 },
        { label: "Submit an Insight for review", to: "/insights", done: mine.some((i) => i.workflow_status !== "draft") },
      ],
    };
  }
  // Plain viewer (read-only, non-board)
  return {
    title: "Getting started",
    subtitle: "Here's where to find things in GovFlowState.",
    items: [
      { label: "Sign in to GovFlowState", done: true },
      { label: "Review your work", to: "/my-work", done: true },
      { label: "Browse Insights", to: "/insights" },
      { label: "See upcoming meetings", to: "/meetings" },
    ],
  };
}

export function GettingStarted({ user, uid, insights, meetings, isBoard }) {
  const role = user?.role;
  const caps = user?.capabilities || [];
  const [hidden, setHidden] = useState(() => localStorage.getItem(KEY(uid)) === "1");

  // Board-only users use Forum View — never show the staff checklist.
  if (role === "viewer" && isBoard && caps.length === 0) return null;

  const { title, subtitle, items } = buildChecklist({ role, caps, uid, insights, meetings });
  const done = items.filter((i) => i.done).length;
  const allDone = items.every((i) => i.done);
  if (allDone) return null; // auto-hide once every applicable starter item is complete

  if (hidden) {
    return (
      <div className="mb-5">
        <button data-testid="show-getting-started"
          onClick={() => { localStorage.removeItem(KEY(uid)); setHidden(false); }}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-civic hover:underline focus-visible:underline">
          <Rocket className="h-3.5 w-3.5" /> Show getting started
        </button>
      </div>
    );
  }

  const hide = () => { localStorage.setItem(KEY(uid), "1"); setHidden(true); };

  return (
    <Card data-testid="getting-started" className="mb-5 border-brand-civic/30 bg-gradient-to-br from-brand-civic-50/60 to-white">
      <section aria-labelledby="getting-started-heading">
        <div className="flex items-start gap-3">
          <span className="h-9 w-9 rounded-lg bg-brand-navy flex items-center justify-center shrink-0">
            <Rocket className="h-4.5 w-4.5 text-brand-gold" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h2 id="getting-started-heading" className="font-display font-bold text-brand-ink text-base">{title}</h2>
              <span className="text-xs font-semibold text-slate-400" data-testid="getting-started-progress">{done}/{items.length}</span>
            </div>
            <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>
          </div>
          <button onClick={hide} data-testid="hide-getting-started" aria-label="Hide getting started"
            className="text-slate-400 hover:text-brand-navy transition-colors shrink-0"><X className="h-4 w-4" /></button>
        </div>
        <ul role="list" className="mt-4 space-y-1.5">
          {items.map((it, idx) => {
            const Icon = it.done ? CheckCircle2 : Circle;
            const inner = (
              <>
                <Icon className={it.done ? "h-4 w-4 text-brand-teal shrink-0" : "h-4 w-4 text-slate-300 shrink-0"} aria-hidden="true" />
                <span className={it.done ? "text-slate-500 line-through" : "text-brand-ink font-medium"}>{it.label}</span>
                <span className="sr-only">{it.done ? " — done" : " — not started"}</span>
                {it.to && !it.done && <ArrowRight className="h-3.5 w-3.5 text-slate-300 ml-auto shrink-0" aria-hidden="true" />}
              </>
            );
            const cls = "flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm";
            return (
              <li key={idx} data-testid={`gs-item-${idx}`}>
                {it.to && !it.done
                  ? <Link to={it.to} data-testid={`gs-link-${idx}`} aria-label={it.label}
                      className={`${cls} border border-transparent hover:border-brand-civic/40 hover:bg-white transition-all`}>{inner}</Link>
                  : <div className={cls}>{inner}</div>}
              </li>
            );
          })}
        </ul>
      </section>
    </Card>
  );
}
